// PWP Assistant admin views (GET /admin/assistant/unanswered, /unanswered/groups, /stats): wire types and
// strict response normalisers. Shared by the RTK Query slice and the two tabs, and import-free so `unit/`
// can run it in plain Node.

export const ASSISTANT_STATUSES = [
  'answered',
  'smalltalk',
  'restricted',
  'plan_locked',
  'off_topic',
  'not_covered',
  'unavailable',
] as const
export type AssistantStatus = (typeof ASSISTANT_STATUSES)[number]

export interface AssistantCompanyRef {
  id: string
  /** null = the company row was not found. */
  name: string | null
}

export interface UnansweredTopic {
  topicId: string
  /** null = the id is not in the loaded pack. */
  title: string | null
}

export interface UnansweredItem {
  turnId: string
  conversationId: string
  createdAt: string
  /** Redacted user text; null once the retention job has removed it. */
  question: string | null
  company: AssistantCompanyRef
  topics: UnansweredTopic[]
  packVersion: string | null
}

export interface UnansweredPage {
  items: UnansweredItem[]
  pageInfo: { nextCursor: string | null; hasNextPage: boolean; limit: number }
}

export interface UnansweredGroup {
  /** null = the "no topic guess" bucket. */
  topicId: string | null
  title: string | null
  count: number
}

export interface UnansweredGroups {
  total: number
  groups: UnansweredGroup[]
}

export interface AssistantCompanyUsage {
  company: AssistantCompanyRef
  messages: number
  costCents: number
}

export interface AssistantAbuseRow {
  company: AssistantCompanyRef
  offTopic: number
  injection: number
}

export interface AssistantStats {
  range: { from: string; to: string; companyId: string | null }
  messages: number
  byStatus: Record<AssistantStatus, number>
  feedback: { up: number; down: number; upRatio: number | null }
  costCents: number
  tokens: { input: number; output: number; cacheRead: number; cacheWrite: number }
  cacheHitRate: number | null
  perDay: { day: string; messages: number }[]
  topCompanies: { byMessages: AssistantCompanyUsage[]; byCost: AssistantCompanyUsage[] }
  abuse: AssistantAbuseRow[]
}

/** `from` / `to` are UTC calendar days, `YYYY-MM-DD`. */
export interface AssistantRangeQuery {
  from: string
  to: string
  companyId?: string
}

export interface UnansweredQuery extends AssistantRangeQuery {
  /** A pack topic id, or `none` for turns with no topic guess. */
  topic?: string
  cursor?: string
  limit?: number
}

export interface AssistantStatsQuery extends AssistantRangeQuery {
  top?: number
}

export const MALFORMED_ASSISTANT_VIEW = 'Malformed assistant view response'

type Dict = Record<string, unknown>
const isDict = (value: unknown): value is Dict => typeof value === 'object' && value !== null && !Array.isArray(value)
const malformed = (): never => {
  throw new Error(MALFORMED_ASSISTANT_VIEW)
}

const dict = (value: unknown): Dict => (isDict(value) ? value : malformed())
const list = <T>(value: unknown, each: (raw: unknown) => T): T[] =>
  Array.isArray(value) ? value.map(each) : malformed()
const text = (value: unknown): string => (typeof value === 'string' && value ? value : malformed())
const nullableText = (value: unknown): string | null =>
  value === null ? null : typeof value === 'string' ? value : malformed()
const flag = (value: unknown): boolean => (typeof value === 'boolean' ? value : malformed())
/** A money or token amount: finite and not negative. */
const amount = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : malformed()
const count = (value: unknown): number => (Number.isInteger(value) ? amount(value) : malformed())
const ratio = (value: unknown): number | null =>
  value === null ? null : amount(value) <= 1 ? amount(value) : malformed()
const day = (value: unknown): string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : malformed()
const timestamp = (value: unknown): string =>
  typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : malformed()

const company = (raw: unknown): AssistantCompanyRef => {
  const value = dict(raw)
  return { id: text(value.id), name: nullableText(value.name) }
}

const unansweredItem = (raw: unknown): UnansweredItem => {
  const value = dict(raw)
  return {
    turnId: text(value.turnId),
    conversationId: text(value.conversationId),
    createdAt: timestamp(value.createdAt),
    question: nullableText(value.question),
    company: company(value.company),
    topics: list(value.topics, (topic) => {
      const entry = dict(topic)
      return { topicId: text(entry.topicId), title: nullableText(entry.title) }
    }),
    packVersion: nullableText(value.packVersion),
  }
}

/**
 * Every normaliser here throws on anything that is not the contract's shape. A body this page cannot read
 * becomes a failed load - never a list that looks empty or a tile that says 0.
 */
export const normalizeUnansweredPage = (raw: unknown): UnansweredPage => {
  const value = dict(raw)
  const pageInfo = dict(value.pageInfo)
  const nextCursor = nullableText(pageInfo.nextCursor)
  const hasNextPage = flag(pageInfo.hasNextPage)
  // "More rows" with no cursor to ask for them is a page that cannot be continued.
  if (hasNextPage && !nextCursor) return malformed()
  return {
    items: list(value.items, unansweredItem),
    pageInfo: { nextCursor, hasNextPage, limit: count(pageInfo.limit) },
  }
}

export const normalizeUnansweredGroups = (raw: unknown): UnansweredGroups => {
  const value = dict(raw)
  return {
    total: count(value.total),
    groups: list(value.groups, (group) => {
      const entry = dict(group)
      return {
        topicId: entry.topicId === null ? null : text(entry.topicId),
        title: nullableText(entry.title),
        count: count(entry.count),
      }
    }),
  }
}

const companyUsage = (raw: unknown): AssistantCompanyUsage => {
  const value = dict(raw)
  return { company: company(value.company), messages: count(value.messages), costCents: amount(value.costCents) }
}

export const normalizeAssistantStats = (raw: unknown): AssistantStats => {
  const value = dict(raw)
  const range = dict(value.range)
  const statuses = dict(value.byStatus)
  const feedback = dict(value.feedback)
  const tokens = dict(value.tokens)
  const topCompanies = dict(value.topCompanies)
  return {
    range: { from: day(range.from), to: day(range.to), companyId: nullableText(range.companyId) },
    messages: count(value.messages),
    byStatus: Object.fromEntries(ASSISTANT_STATUSES.map((status) => [status, count(statuses[status])])) as Record<
      AssistantStatus,
      number
    >,
    feedback: { up: count(feedback.up), down: count(feedback.down), upRatio: ratio(feedback.upRatio) },
    costCents: amount(value.costCents),
    tokens: {
      input: count(tokens.input),
      output: count(tokens.output),
      cacheRead: count(tokens.cacheRead),
      cacheWrite: count(tokens.cacheWrite),
    },
    cacheHitRate: ratio(value.cacheHitRate),
    perDay: list(value.perDay, (entry) => {
      const point = dict(entry)
      return { day: day(point.day), messages: count(point.messages) }
    }),
    topCompanies: {
      byMessages: list(topCompanies.byMessages, companyUsage),
      byCost: list(topCompanies.byCost, companyUsage),
    },
    abuse: list(value.abuse, (entry) => {
      const row = dict(entry)
      return { company: company(row.company), offTopic: count(row.offTopic), injection: count(row.injection) }
    }),
  }
}
